import { WarningCircle } from "@phosphor-icons/react"
import { useMemo } from "react"

import {
  ADAPTATION_ANSWER_ORDER,
  ADAPTATION_ORDER,
  adaptationLabels,
  type FashionPreorderDevelopment,
  KNOWN_SHARE_OPTION,
  unmeasuredNote,
} from "~/components/demand-2/shared/preorderDevelopment"
import {
  pinnedAxisMax,
  signedPct,
  signedUnits,
} from "~/components/demand-2/shared/demandBreakdownShared"
import {
  KnownShareBar,
  type KnownShareCopy,
  knownShareSeries,
  PREORDER_WEEKLY_Y_AXES,
  PreorderAdaptationQuestion,
  PreorderChartPanel,
} from "~/components/demand-2/shared/PreorderBreakdownLayout"
import { SizingSpinner } from "~/components/demand-2/shared/SizingSpinner"
import SuperChart, {
  type DateChartSeries,
  type StackedBarChartData,
} from "~/components/reusable/graphs/SuperChart"
import { useChartBaseColors, useChartSeriesConfig } from "~/constants/colors"
import { ChartType, DateType, LegendAlignment, TodayPlotLine } from "~/constants/graphs"
import { formatDecimalToPercent } from "~/utils/formatting"
import type { AdaptationMode } from "~/utils/demandPreorderDevelopment"
import { useAllSlicesGuard } from "~/components/demand-2/shared/useAllSlicesGuard"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { isNarrowScope } from "~/utils/demandFraming"
import { formatName } from "~/utils/namingUtils"

/**
 * Where the fashion "Pre-order development" number comes from. Figures are not recomputed here:
 * they come from `useFashionPreorderDevelopment`, the same call the row makes.
 */

export const UnmeasuredNote = ({ text }: { text: string | null }) =>
  text ? (
    <div className="flex items-start gap-2 paragraph-sm text-fg-warning-icon">
      <WarningCircle className="mt-0.5 size-4 shrink-0" weight="fill" />
      <span>{text}</span>
    </div>
  ) : null

/** A function, not a constant: `formatName` reads tenant naming at call time. */
const fashionAdaptationNotes = (
  remainingShare: number,
): Record<(typeof ADAPTATION_ORDER)[number], string> => {
  const remaining = formatDecimalToPercent(remainingShare, 1)
  return {
    closeBook: `Expect no further ${formatName("preorder", true)} on top of what is confirmed`,
    maintain: `Assume the collection lands where the baseline did, but will never below the confirmed ${formatName("preorder", true)} for each variant`,
    adaptChange: `Expect the remaining ${remaining} to be similar to the comparable collection`,
    adaptTrend: `Expect the remaining ${remaining} to follow the overall development`,
  }
}

const FASHION_KNOWN_SHARE_COPY: KnownShareCopy = {
  label: KNOWN_SHARE_OPTION.value!.label,
  resetTitle: (estimated) => `Back to the curve's ${estimated}`,
}

const FashionPreorderDevelopmentBreakdown = ({
  persistKey,
  data,
}: {
  persistKey: string
  data: FashionPreorderDevelopment
}) => {
  const {
    totals,
    knownShare,
    measuredKnownShare,
    overrideShare,
    paceReadable,
    paceDiff,
    pacePct,
    projected,
    projectedByMode,
    measuredProjectedByMode,
    development,
    pace,
    currentWeek,
    mode,
    modeAnswered,
    modeInherited,
    scope,
    clearMode,
    setKnownShareOverride,
    unmeasured,
  } = data
  const chartBaseColors = useChartBaseColors()
  const seriesConfig = useChartSeriesConfig()
  // The route's first collection, the id every answer is written against (`useAdaptationAnswer`).
  const { collectionIds } = useDemand2Scope()
  const guard = useAllSlicesGuard({
    question: "preorderDevelopment",
    collectionId: collectionIds[0] ?? 0,
    scope,
    // The picker's own wording, which differs by kind, not the generic option label.
    labelFor: (answer) => adaptationLabels("fashion")[answer as AdaptationMode] ?? null,
  })

  const preorders = formatName("preorder", true)
  const columns = useMemo(
    () => [`Baseline ${preorders}`, "Expected by now", "Confirmed now", "Projected collection"],
    [preorders],
  )

  const paceLabel = paceReadable
    ? `${signedUnits(paceDiff)}${pacePct == null ? "" : ` (${signedPct(pacePct)})`} vs expected`
    : null

  const series = useMemo(
    (): StackedBarChartData[] => [
      {
        name: `Confirmed ${preorders}`,
        type: "column",
        color: seriesConfig.confirmedPreOrders.color,
        // The projection is floored at the book, so the last column's base never needs capping.
        data: [
          Math.round(totals.comparables),
          Math.round(totals.expectedKnownNow),
          Math.round(totals.knownNow),
          Math.round(totals.knownNow),
        ],
        pointLabels: [null, null, paceLabel, null],
      },
      {
        name: `Expected ${preorders}`,
        type: "column",
        color: seriesConfig.expectedPreOrders.color,
        data: [null, null, null, Math.round(development)],
      },
    ],
    [totals, development, paceLabel, preorders, seriesConfig],
  )

  // Pinned so switching answers or dragging the share moves only the columns that change, not the
  // whole axis. Sized on the measured share, so an answer the share doesn't move holds still.
  const yAxisMax = useMemo(
    () =>
      pinnedAxisMax(
        Math.max(
          totals.comparables,
          totals.expectedKnownNow,
          totals.knownNow,
          projected,
          ...ADAPTATION_ORDER.map((m) => measuredProjectedByMode[m]),
        ),
      ),
    [totals, projected, measuredProjectedByMode],
  )

  const weeklySeries = useMemo((): DateChartSeries[] => {
    if (pace.length === 0) return []
    return [
      {
        name: `Expected ${preorders} confirmed`,
        type: "column",
        colors: [seriesConfig.expectedPreOrders.color],
        data: pace.map((point) => ({
          milliseconds: Date.parse(point.weekStart),
          value: Math.round(projected * point.fractionKnown),
        })),
      },
      {
        name: `Confirmed now`,
        type: "spline",
        colors: [seriesConfig.confirmedPreOrders.color],
        data: pace.map((point) => ({
          milliseconds: Date.parse(point.weekStart),
          // Only at the planning week.
          value:
            Date.parse(point.weekStart) === currentWeek.getTime()
              ? Math.round(totals.knownNow)
              : null,
        })),
      },
      knownShareSeries(
        `% of ${preorders} confirmed`,
        chartBaseColors.contrast,
        pace.map((point) => ({ ms: Date.parse(point.weekStart), share: point.fractionKnown })),
      ),
    ]
  }, [pace, projected, totals.knownNow, currentWeek, preorders, chartBaseColors, seriesConfig])

  if (data.isLoading) return <SizingSpinner className="h-[420px] w-full" />
  const measuredAny = totals.comparables > 0 || totals.knownNow > 0
  const note = unmeasuredNote(unmeasured, measuredAny)
  if (!measuredAny) {
    if (note) return <UnmeasuredNote text={note} />
    return (
      <p className="paragraph-sm text-fg-subtle">
        No {preorders} on this collection&apos;s baseline, and nothing confirmed yet.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <UnmeasuredNote text={note} />
        <KnownShareBar
          knownShare={knownShare}
          measuredKnownShare={measuredKnownShare}
          overrideShare={overrideShare}
          onChange={setKnownShareOverride}
          readOnly={scope.kind === "variant"}
          copy={FASHION_KNOWN_SHARE_COPY}
          lockedReason={
            mode === "closeBook"
              ? `"Close the book" treats every ${formatName("preorder", false)} as confirmed already.`
              : undefined
          }
        />
        <PreorderChartPanel
          persistKey={persistKey}
          idPrefix="fashion-preorder"
          series={series}
          columns={columns}
          yAxisMax={yAxisMax}
          weekly={
            weeklySeries.length === 0 ? null : (
              <SuperChart
                key="weekly"
                type={ChartType.TimeSeries}
                data={weeklySeries}
                height={340}
                legendAlignment={LegendAlignment.Bottom}
                dateType={DateType.Weekly}
                todayPlotLine={TodayPlotLine.Detailed}
                todayPlotLineValue={currentWeek.getTime()}
                yAxis={PREORDER_WEEKLY_Y_AXES}
              />
            )
          }
          footer={
            weeklySeries.length === 0 && (
              <p className="paragraph-sm text-fg-subtle px-1 pb-1">
                No {preorders} curve for this collection, so the week-by-week pace cannot be drawn.
              </p>
            )
          }
        />
      </section>
      <PreorderAdaptationQuestion
        kind="fashion"
        idPrefix="fashion-preorder"
        modes={ADAPTATION_ANSWER_ORDER}
        knownShare={knownShare}
        notes={fashionAdaptationNotes(Math.max(1 - knownShare, 0))}
        baseline={totals.baselinePreorders}
        projectedByMode={projectedByMode}
        mode={mode}
        modeAnswered={modeAnswered}
        modeInherited={modeInherited}
        narrow={isNarrowScope(scope)}
        paceNote={
          paceReadable
            ? null
            : `Only ${formatDecimalToPercent(knownShare, 1)} of the collection is confirmed by now, too little to read the pace as a trend. The two adapting answers therefore land where "Match baseline" does.`
        }
        setMode={guard.write}
        clearMode={clearMode}
      />
      {guard.dialog}
    </div>
  )
}

export default FashionPreorderDevelopmentBreakdown
